import { useEffect } from 'react';
import { Side } from '@/common/constants';
import { useDialogStore } from '@/components/dialogs';
import { MARKET_MENU_CLASS, marketMenuToggle } from '@/feature/market_info/market_select/marketMenuShortcut.ts';
import { orderFormShortcut } from '@/feature/order_entry/hooks/useOrderFormShortcuts.ts';
import useAppStore from '../../store/rootStore.ts';
import { closeSelectedPosition } from '../account/hooks/useClosePositionShortcut.ts';
import { type GeneralShortcutId, isModalOpen, isTypingTarget, matchShortcut } from './shortcuts.ts';

// A handler returns false when nothing answered, so the key goes on to the browser.
const handlers: Record<GeneralShortcutId, () => unknown> = {
  openMarkets: marketMenuToggle.call,
  long: () => orderFormShortcut.call({ kind: 'side', side: Side.long }),
  short: () => orderFormShortcut.call({ kind: 'side', side: Side.short }),
  toggleMarketLimit: () => orderFormShortcut.call({ kind: 'toggleMarketLimit' }),
  closePosition: closeSelectedPosition.call,
  openSettings: () => useDialogStore.getState().openDialog('userPreferencesDrawer'),
  // Same gating as the header's Deposit / Withdraw buttons.
  deposit: () => {
    if (useAppStore.getState().isAtLeastAuthenticated()) useDialogStore.getState().openDialog('depositCollateralDialog');
  },
  withdraw: () => {
    if (useAppStore.getState().isFullyAuthorized()) useDialogStore.getState().openDialog('withdrawDialog');
  },
};

/** App-wide keyboard shortcuts (GENERAL_SHORTCUTS). Mount once, in the root layout. */
export function useGeneralShortcuts(isTradePage: boolean) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      const shortcut = matchShortcut(event);
      if (!shortcut || (shortcut.tradeOnly && !isTradePage)) return;
      if (!shortcut.mod && isTypingTarget(event)) return;
      // ⌘K also works with the market menu (itself a modal popover) open, to close it — but not over other modals.
      if (isModalOpen(shortcut.mod ? `.${MARKET_MENU_CLASS}` : undefined)) return;
      if (handlers[shortcut.id]() === false) return;
      event.preventDefault();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isTradePage]);
}
